import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"
import { FiArchive, FiPlus, FiMail } from "react-icons/fi"

import "./index.scss"
import useT from "utils/hooks/useT"
import { ConfirmDialog, Popup } from "components"
import { members as membersState, permissions, projects } from "state_management"
import { QA, QA_MANAGER } from "state_management/lims/limsCapabilities"
import QaMemberConfig from "./QaMemberConfig"

const LAB_CAPABILITIES = [
  { id: "analyst", labelKey: "limsCapAnalyst" },
  { id: "reviewer", labelKey: "limsCapReviewer" },
  { id: "supervisor", labelKey: "limsCapSupervisor" },
]

const ROLE_LABEL_KEYS = { MEMBER: "member", PLANNER: "planner", VIEW_ONLY: "viewOnly" }

// LIMS › QA team (SPEC-058 A, Plannertech-user#4115). The quality column's own staff page: the
// QA Manager (a MEMBER-role member holding `qa-manager`) assigns and revokes the `qa` role with
// an e-signature, adds QA members (always the Member planning role), archives QA-role holders
// and sends them a password mail. It REPLACES the QA Manager's old entry into Setup › Team,
// where every planning field was read-only for them without saying why — here nothing is
// ghosted because the page only holds what they can do. The Planner may open the page read-only
// to see who holds QA; nobody else sees the menu item (LimsScreen gates the section).
//
// Every decision helper below is a mirror of a server rule (permissions.js) and the server stays
// the law: a refused call is toasted from the message layer.
export default function QaTeam() {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const canManage = useSelector(permissions.selectCanManageQaTeam)
  const authRole = useSelector((state) => state.auth.role)
  const authMemberId = useSelector((state) => state.auth.memberId)
  const showLims = useSelector((state) => state.teamStatus.showLims)
  // #4153 — the page lists the QUALITY COLUMN, not the whole team. It used to show every
  // non-archived member, which is what the issue reports: a staff page for QA that was really
  // the team list with QA controls bolted on.
  //
  // `qa-manager` is included alongside `qa` deliberately. "Only QA members" reads as the quality
  // column's own staff, and a QA Manager who does not personally hold `qa` would otherwise
  // vanish from the very page they administer.
  const members = Object.values(useSelector((state) => state.members))
    .filter((member) => !member.archived)
    .filter((member) => {
      const capabilities = member.qcCapabilities || []
      return capabilities.includes(QA) || capabilities.includes(QA_MANAGER)
    })
    .sort((a, b) => (a.initials || "").localeCompare(b.initials || ""))

  const [adding, setAdding] = useState(false)
  const [activeArchive, setActiveArchive] = useState(null)

  useEffect(() => {
    dispatch(membersState.fetch())
  }, [dispatch])

  const holds = (member, capability) => (member.qcCapabilities || []).includes(capability)

  // #4153 — there is deliberately NO "assign QA" / "remove QA" here any more. The role is set
  // when a QA member is created (the Add-QA-member form below) and ends when they are archived;
  // the server refuses a change in either direction. Being able to GRANT it was a backdoor to
  // something else entirely: a QA Manager may archive and password-mail QA bearers, so granting
  // the role to an arbitrary colleague handed the granter that power over them.
  //
  // Removed rather than ghosted: no actor can perform the action any more, so a disabled control
  // would advertise a permission nobody can ever hold.

  const handleArchive = () => {
    dispatch(membersState.archive(activeArchive.id))
      .then(() => dispatch(projects.fetch()))
      // The toast (incl. the translated 409 MEMBER_HOLDS_QA_ROLE fallback) comes from the message
      // layer — just close the dialog either way.
      .catch(() => {})
      .finally(() => setActiveArchive(null))
  }

  return (
    <div className="QaTeam">
      {activeArchive && (
        <ConfirmDialog onCancel={() => setActiveArchive(null)} onConfirm={handleArchive}>
          <h4>{t("confirmArchivalOfMember", { initials: activeArchive.initials })}</h4>
        </ConfirmDialog>
      )}
      {adding && (
        <Popup nonScrollable onCancel={() => setAdding(false)}>
          <QaMemberConfig onClose={() => setAdding(false)} />
        </Popup>
      )}

      <div className="QaTeam-header">
        <div>
          <h2>{t("limsQaTeam")}</h2>
          <p className="QaTeam-intro">{canManage ? t("limsQaTeamIntro") : t("limsQaTeamPlannerReadOnly")}</p>
        </div>
        {canManage && (
          <Button variant="primary" onClick={() => setAdding(true)}>
            <FiPlus /> {t("limsQaTeamAddMember")}
          </Button>
        )}
      </div>

      <Table hover responsive className="QaTeam-table">
        <thead>
          <tr>
            <th>{t("initials")}</th>
            <th>{t("emailLabel")}</th>
            <th>{t("role")}</th>
            <th>{t("limsQaTeamLabQualifications")}</th>
            <th>{t("limsQaTeamQaRole")}</th>
            {canManage && <th className="QaTeam-actions-head" />}
          </tr>
        </thead>
        <tbody>
          {members.map((member) => {
            const isQaManager = holds(member, QA_MANAGER)
            const isQa = holds(member, QA)
            const mail = permissions.passwordMailDecision({
              actorRole: authRole,
              actorIsQaManager: canManage,
              actorMemberId: authMemberId,
              target: member,
            })
            const archive = permissions.archiveDecision({
              actorRole: authRole,
              actorIsQaManager: canManage,
              actorMemberId: authMemberId,
              showLims,
              target: member,
            })
            return (
              <tr key={member.id}>
                <td className="QaTeam-initials">{member.initials}</td>
                <td>{member.email}</td>
                <td>{t(ROLE_LABEL_KEYS[member.role] || "member")}</td>
                <td>
                  {LAB_CAPABILITIES.filter(({ id }) => holds(member, id)).map(({ id, labelKey }) => (
                    <span key={id} className="QaTeam-badge QaTeam-badge-lab">
                      {t(labelKey)}
                    </span>
                  ))}
                </td>
                <td>
                  {isQaManager && (
                    <span className="QaTeam-badge QaTeam-badge-qa" title={t("limsCapQaManager")}>
                      QM
                    </span>
                  )}
                  {isQa && (
                    <span className="QaTeam-badge QaTeam-badge-qa" title={t("limsCapQa")}>
                      QA
                    </span>
                  )}
                </td>
                {canManage && (
                  <td className="QaTeam-actions">
                    {mail.allowed && (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        title={t("sendResetMail")}
                        onClick={() => dispatch(membersState.sendPasswordMail(member.id)).catch(() => {})}
                      >
                        <FiMail /> {t("sendResetMail")}
                      </Button>
                    )}
                    {archive.allowed && (
                      <FiArchive
                        className="QaTeam-archive"
                        title={t("archiveMember")}
                        onClick={() => setActiveArchive(member)}
                      />
                    )}
                    {!archive.allowed && archive.tooltipKey && (
                      <FiArchive className="QaTeam-archive blocked-action-icon" title={t(archive.tooltipKey)} />
                    )}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </Table>
    </div>
  )
}
